/**
 * SC-03 — Replace selected shape text (AIVA-6754).
 *
 * Writes an AiVA reply into the text of the shape the user has selected,
 * replacing what was there. The action bar shows "Replace selection" next to
 * "Insert into slide", greyed out unless a shape with text was selected when the
 * reply arrived; the selection is checked again here at click time, because the
 * user may have changed it since.
 *
 * Targets the shape the user actually SELECTED, not the first text shape on the
 * slide. The spec's G-1 shipped "first text-bearing shape" as best effort because
 * the prototype could not read the selection; `getSelectedShapes`
 * (PowerPointApi 1.5) can, so a slide with a title and a body box replaces the
 * box the user picked. With several shapes selected, the first that has text is
 * used. Nothing selected at click time is reported, never guessed — overwriting
 * the title the user did not pick is the outcome G-1 warned about.
 *
 * Highlighted text inside a shape is replaced on its own and the rest of the
 * shape is kept (AIVA-9245).
 *
 * The text is cleaned the same way Insert into slide cleans it (`toSlideText`:
 * no Markdown markers, no "- " bullet prefixes). The shape keeps its own
 * formatting; only its text changes, and one Ctrl+Z restores it.
 */

import { NOTHING_TO_INSERT_ERROR, POWERPOINT_UNAVAILABLE_ERROR, toSlideText } from './insertIntoSlide';

/** Thrown when no selected shape has text to replace (or the host cannot say). */
export const NO_TEXT_SHAPE_SELECTED_ERROR = 'No shape with text is selected';

export interface ReplaceSelectionResult {
  readonly replaced: true;
  /** Id of the shape whose text was replaced, when the host reports it. */
  readonly shapeId?: string;
}

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function ppt(): typeof PowerPoint {
  return (globalThis as { PowerPoint: typeof PowerPoint }).PowerPoint;
}

/**
 * Whether `shape` holds text. Read in its own sync: a picture, chart or line
 * has no text frame, and asking one fails the whole batch.
 */
async function shapeHasText(context: PowerPoint.RequestContext, shape: PowerPoint.Shape): Promise<boolean> {
  try {
    const frame = shape.textFrame;
    frame.load('hasText');
    await context.sync();
    return frame.hasText;
  } catch {
    return false;
  }
}

/** The first of `shapes` that holds text, checked one at a time, in order. */
async function firstShapeWithText(
  context: PowerPoint.RequestContext,
  shapes: readonly PowerPoint.Shape[],
  from = 0
): Promise<PowerPoint.Shape | null> {
  const shape = shapes[from];
  if (!shape) return null;
  if (await shapeHasText(context, shape)) return shape;
  return firstShapeWithText(context, shapes, from + 1);
}

/** The selected shapes, or [] when nothing is selected or the host lacks the API. */
async function readSelectedShapes(context: PowerPoint.RequestContext): Promise<PowerPoint.Shape[]> {
  try {
    const shapes = context.presentation.getSelectedShapes();
    shapes.load('items/id');
    await context.sync();
    return shapes.items;
  } catch {
    return []; // PowerPointApi 1.5 not available on this host
  }
}

/** The highlighted text range, or null when nothing is highlighted. */
async function readHighlightedRange(
  context: PowerPoint.RequestContext
): Promise<PowerPoint.TextRange | null> {
  try {
    const range = context.presentation.getSelectedTextRangeOrNullObject();
    range.load('text');
    await context.sync();
    return !range.isNullObject && range.text?.trim() ? range : null;
  } catch {
    return null; // PowerPointApi 1.5 not available on this host
  }
}

/**
 * Replace the highlighted text, or else the selected shape's text, with `text`.
 *
 * Throws {@link NO_TEXT_SHAPE_SELECTED_ERROR}, {@link POWERPOINT_UNAVAILABLE_ERROR}
 * or {@link NOTHING_TO_INSERT_ERROR}; any other error is a host failure.
 */
export async function replaceSelectedShapeText(text: string): Promise<ReplaceSelectionResult> {
  if (!hasPpt()) throw new Error(POWERPOINT_UNAVAILABLE_ERROR);
  const slideText = toSlideText(text);
  if (!slideText) throw new Error(NOTHING_TO_INSERT_ERROR);

  return await ppt().run(async (context) => {
    const highlighted = await readHighlightedRange(context);
    if (highlighted) {
      highlighted.text = slideText;
      await context.sync();
      return { replaced: true as const };
    }
    const target = await firstShapeWithText(context, await readSelectedShapes(context));
    if (!target) throw new Error(NO_TEXT_SHAPE_SELECTED_ERROR);
    target.textFrame.textRange.text = slideText;
    await context.sync();
    return { replaced: true as const, ...(target.id ? { shapeId: target.id } : {}) };
  });
}
